import React from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";

const CTASection = () => {
    const controls = useAnimation();
    const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: false });

    React.useEffect(() => {
        if (inView) {
            controls.start("visible");
        }
    }, [controls, inView]);

    return (
        <SectionContainer ref={ref}>
            <BackgroundOverlay />
            <GlowOrb className="left" />
            <GlowOrb className="right" />

            <Container>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={controls}
                    variants={{
                        hidden: { opacity: 0, y: 30 },
                        visible: {
                            opacity: 1,
                            y: 0,
                            transition: { duration: 0.8 },
                        },
                    }}
                >
                    <Subtitle>Let's Create Together</Subtitle>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={controls}
                    variants={{
                        hidden: { opacity: 0, y: 30 },
                        visible: {
                            opacity: 1,
                            y: 0,
                            transition: { duration: 0.8, delay: 0.2 },
                        },
                    }}
                >
                    <Title>Ready to Create Something Exceptional?</Title>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={controls}
                    variants={{
                        hidden: { opacity: 0, y: 30 },
                        visible: {
                            opacity: 1,
                            y: 0,
                            transition: { duration: 0.8, delay: 0.4 },
                        },
                    }}
                >
                    <Description>
                        Transform your vision into stunning reality. Contact us today to
                        discuss your custom grillz or jewelry project.
                    </Description>
                </motion.div>

                <ButtonGroup
                    initial={{ opacity: 0, y: 30 }}
                    animate={controls}
                    variants={{
                        hidden: { opacity: 0, y: 30 },
                        visible: {
                            opacity: 1,
                            y: 0,
                            transition: { duration: 0.8, delay: 0.6 },
                        },
                    }}
                >
                    <PrimaryButton
                        to="/contact"
                        whileHover={{
                            y: -5,
                            boxShadow: "0 15px 35px rgba(255, 215, 0, 0.4)",
                        }}
                    >
                        Get Started
                    </PrimaryButton>
                    <SecondaryButton
                        to="/services"
                        whileHover={{
                            y: -5,
                        }}
                    >
                        View Services
                    </SecondaryButton>
                </ButtonGroup>
            </Container>
        </SectionContainer>
    );
};

// Keyframes
const pulse = keyframes`
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.1);
  }
`;

// Styled Components
const SectionContainer = styled.section`
  position: relative;
  padding: var(--spacing-xl) 0;
  background: var(--darker-bg);
  overflow: hidden;
`;

const BackgroundOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(255, 215, 0, 0.03) 0%,
    transparent 50%,
    rgba(255, 215, 0, 0.03) 100%
  );
  z-index: 0;
`;

const GlowOrb = styled.div`
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(
    circle at center,
    rgba(255, 215, 0, 0.08) 0%,
    transparent 70%
  );
  animation: ${pulse} 6s ease-in-out infinite;
  z-index: 0;

  &.left {
    top: -100px;
    left: -100px;
  }

  &.right {
    bottom: -100px;
    right: -100px;
    animation-delay: 3s;
  }

  @media (max-width: 768px) {
    width: 250px;
    height: 250px;
  }
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  text-align: center;
  position: relative;
  z-index: 1;
`;

const Subtitle = styled.span`
  color: var(--gold);
  text-transform: uppercase;
  letter-spacing: 4px;
  font-size: 0.9rem;
  margin-bottom: var(--spacing-sm);
  display: block;
`;

const Title = styled.h2`
  font-size: 3rem;
  color: white;
  margin-bottom: var(--spacing-md);
  font-weight: 700;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }

  @media (max-width: 576px) {
    font-size: 1.8rem;
  }
`;

const Description = styled.p`
  font-size: 1.2rem;
  color: #b0b0b0;
  max-width: 600px;
  margin: 0 auto var(--spacing-lg);
  line-height: 1.8;

  @media (max-width: 576px) {
    font-size: 1rem;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: var(--spacing-md);

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-sm);
  }
`;

const PrimaryButton = styled(motion(Link))`
  display: inline-block;
  padding: 16px 40px;
  background: var(--gold-gradient);
  color: var(--black);
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: var(--gold-glow);

  @media (max-width: 576px) {
    width: 100%;
    text-align: center;
  }
`;

const SecondaryButton = styled(motion(Link))`
  display: inline-block;
  padding: 16px 40px;
  background: transparent;
  color: var(--gold);
  border: 1px solid var(--gold);
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 215, 0, 0.1);
  }

  @media (max-width: 576px) {
    width: 100%;
    text-align: center;
  }
`;

export default CTASection;
